import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  DestroyRef,
  ElementRef,
  EventEmitter,
  forwardRef, Host,
  inject,
  Input, Optional,
  Output,
  QueryList,
  OnDestroy,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {ControlValueAccessor, FormGroupDirective, NG_VALUE_ACCESSOR, NgForm} from "@angular/forms";
import {CustomSelectOptionComponent} from "./custom-select-option/custom-select-option.component";
import {CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition} from "@angular/cdk/overlay";
import {SafeHtml} from "@angular/platform-browser";
import {Subscription} from "rxjs";

@Component({
    selector: 'app-custom-select',
    templateUrl: './custom-select.component.html',
    styleUrls: ['./custom-select.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomSelectComponent),
            multi: true
        }
    ],
    standalone: false
})
export class CustomSelectComponent implements ControlValueAccessor, AfterViewInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  @ContentChildren(CustomSelectOptionComponent, { descendants: true }) projectedOptions!: QueryList<CustomSelectOptionComponent>;
  @ViewChildren(CustomSelectOptionComponent) viewOptions!: QueryList<CustomSelectOptionComponent>;

  @ViewChild('trigger') trigger!: CdkOverlayOrigin;
  @ViewChild('input') input!: ElementRef;
  @ViewChild('cdkOverlay') cdkOverlay!: CdkConnectedOverlay;
  @ViewChild('overlayWrapper') overlayWrapper!: ElementRef;

  previewHtml: SafeHtml;

  @Input() placeholder: string = 'Placeholder';
  @Input() items: any[];
  @Input() featuredItems: any[];
  @Input() bindLabel: string;
  @Input() bindValue: string;
  @Input() searchable = false;
  @Input() clearable = true;
  @Input() notFoundText = 'No results found';
  @Input() multiple = false;
  @Input() addAllText: string;
  @Input() addAllSecondaryText: string;
  @Input() disabled = false;
  @Input() appearanceVariant: 'default' | 'member-filter' | 'status-pill' = 'default';
  @Input() overlayPlacement: 'inside' | 'outside' = 'outside';
  @Input() fixedOverlayVerticalPlacement: 'above' | 'below';
  @Input() hideIcons = false;
  @Input() customButtonText: string;
  @Input() isShowCustomButton: boolean = true;
  @Input() isShowAddIcon: boolean;
  @Input() debug = false;
  @Input() maxLength?: number | string | null;
  // Overrides the default dropdown min width (the trigger width), e.g. '300px' for a narrow trigger with long option names
  @Input() panelMinWidth: string | number | null = null;
  // The parent filters/sorts the options itself in response to searchChange; the select only reports the search text
  // (and an empty text when the dropdown closes) instead of hiding non-matching options
  @Input() externalSearch = false;
  // Enter picks the first visible option of an opened searchable single select
  @Input() selectFirstOnEnter = false;
  // Overrides the default panel height limit, e.g. 'none' for a short list that should never scroll
  @Input() panelMaxHeight: string | null = null;

  @Output() change: EventEmitter<any> = new EventEmitter();
  @Output() clear: EventEmitter<void> = new EventEmitter();
  @Output() customButtonClick: EventEmitter<void> = new EventEmitter();
  @Output() searchChange = new EventEmitter<string>();

  autoDeterminedDropdownPosition: 'above' | 'below' = 'below';

  isFormSubmitted = false;
  isOpened = false;
  isNoResults = false;
  allAdded = false;

  selectedOption: CustomSelectOptionComponent;
  selectedOptions: CustomSelectOptionComponent[] = [];

  writeValueTempInitialValue: any = null;
  private formClassObserver?: MutationObserver;
  private optionClickSubscriptions = new Subscription();

  onChange: any = () => {
  }
  onTouch: any = () => {
  }


  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouch = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.cdRef.markForCheck();
  }

  writeValue(value: any): void {
    if (this.debug) console.debug('writeValue called with value', value);

    if (this.multiple) {
      if (this.debug) console.debug('checking in multiple value mode');

      if (value === null || value === undefined) {
        if (this.selectedOptions.length > 0) {
          this.resetOptionsStatus();
        }
        this.saveValueForAfterOptionsExists(null);
        this.cdRef.markForCheck();
        return;
      }

      if (!(value instanceof Array)) {
        if (this.debug) console.debug('Single value detected. Returned');
        return;
      }

      if (!this.getNeededOptionsList()) {
        if (this.debug) console.debug('Value saved for future check', value);
        this.saveValueForAfterOptionsExists(value);
        return;
      }

      if (this.selectedOptions.length > 0) {
        this.resetOptionsStatus();
        if (this.debug) console.debug('Detected selected options. They were cleared');
      }

      this.selectedOptions = this.getNeededOptionsList().filter(e => value.some(v => this.valuesMatch(e.value, v)));

      if (this.selectedOptions.length > 0) {
        if (this.debug) console.debug(`${this.selectedOptions.length} out of ${value.length} options found and set`);
        this.selectedOptions.forEach(option => option.setHidden(true));
        this.saveValueForAfterOptionsExists(null);
      }
    }
    else {
      if (this.debug) console.debug('checking in single value mode');

      if (value instanceof Array) {
        if (this.debug) console.debug('Array detected. Returned');
        return;
      }

      if (this.selectedOption?.value != value) {
        if (this.debug) console.debug('Selected option is different.', this.selectedOption);

        if (!this.getNeededOptionsList()) {
          if (this.debug) console.debug('Value saved for future check', value);
          this.saveValueForAfterOptionsExists(value);
          return;
        }

        if (value === null || value === undefined) {
          if (this.debug) console.debug('Value is empty. Selected option cleared');
          this.removeSelectedOption();
        } else {
          if (this.getNeededOptionsList().length === 0) {
            if (this.debug) console.debug('Options list is empty. Value saved for future check', value);
            this.saveValueForAfterOptionsExists(value);
            return;
          }

          if (this.selectedOption) {
            this.resetOptionStatus(this.selectedOption);
            if (this.debug) console.debug('Detected selected option. It was cleared');
          }

          this.selectedOption = this.getNeededOptionsList().find(e => this.valuesMatch(e.value, value));

          if (this.selectedOption) {
            if (this.debug) console.debug('Needed option found and set');
            this.selectedOption.setSelected(true);
            this.previewHtml = this.selectedOption.sanitizedHtml;
            this.saveValueForAfterOptionsExists(null);
          } else {
            if (this.debug) console.debug('Needed option wasnt found. Value saved for future check', value);
            this.saveValueForAfterOptionsExists(value);
            return;
          }
        }
      }
    }

    this.cdRef.markForCheck();
    if (this.debug) console.debug('writeValueTempInitialValue set to null');
    this.saveValueForAfterOptionsExists(null);
  }

  removeSelectedOption() {
    if (this.selectedOption) this.selectedOption.setSelected(false);
    this.selectedOption = null;
    this.previewHtml = null;
  }

  saveValueForAfterOptionsExists(value: any) {
    this.writeValueTempInitialValue = value;
  }

  resetOptionStatus(option: CustomSelectOptionComponent) {
    option.setSelected(false);
    option.setHidden(false);
  }

  constructor(private cdRef: ChangeDetectorRef,
              private elementRef: ElementRef,
              @Optional() @Host() private ngForm: NgForm,
              @Optional() @Host() private formGroupDir: FormGroupDirective) {
  }

  setIsOpened(value: boolean) {
    if (value && !this.fixedOverlayVerticalPlacement) {
      this.updateAutoDeterminedDropdownPosition();
    }

    if (this.isOpened !== value) {
      this.isOpened = value;
      this.cdRef.markForCheck();
      if (!value) this.resetExternalSearch();
    } else this.isOpened = value;

    if (value && this.cdkOverlay?.overlayRef) {
      // Apply the newly resolved positions before asking the overlay to recalculate.
      // Otherwise the first click uses the positions from the previous change-detection pass.
      this.cdRef.detectChanges();
      this.cdkOverlay.overlayRef.updatePosition();
    }
  }

  openDropdown(): void {
    this.setIsOpened(true);

    // The closed panel uses display: none, so its height cannot be measured
    // until the opened state has been rendered.
    this.cdRef.detectChanges();
    this.cdkOverlay.overlayRef.updatePosition();
  }

  toggleDropdown(): void {
    if (this.isOpened) {
      this.setIsOpened(false);
    } else {
      this.openDropdown();
    }
  }

  updatePreviewHtml() {
    this.previewHtml = Object.create(this.selectedOption.sanitizedHtml);
  }

  ngAfterViewInit() {
      if (this.ngForm) {
        setTimeout(() => {
          this.syncSubmittedState();
          this.cdRef.markForCheck();
        });
        this.ngForm.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.syncSubmittedState(true);
          this.cdRef.markForCheck();
        });
      }
      if (this.formGroupDir) {
        setTimeout(() => {
          this.syncSubmittedState();
          this.cdRef.markForCheck();
        });
        this.formGroupDir.ngSubmit.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
          this.syncSubmittedState(true);
          this.cdRef.markForCheck();
        });
      }

    this.observeClosestFormSubmittedClass();


    const optionsList = this.getNeededOptionsList();

    this.bindOptionClickHandlers(optionsList);

    optionsList.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((options: QueryList<CustomSelectOptionComponent>) => {
      if (this.debug) console.debug("Options list was changed", options);
      this.bindOptionClickHandlers(options);

      if (this.writeValueTempInitialValue != null) {
        if (this.debug) console.debug("Trying to write saved value again", this.writeValueTempInitialValue);
        this.writeValue(this.writeValueTempInitialValue);
      }
      setTimeout(() => {
        this.isNoResults = optionsList.length === 0;
        this.cdRef.markForCheck();
      }, 0);
    });

    if (this.writeValueTempInitialValue != null) {
      if (this.debug) console.debug("Trying to write saved value again", this.writeValueTempInitialValue);
      this.writeValue(this.writeValueTempInitialValue);
    }

    setTimeout(() => {
      this.isNoResults = optionsList.length === 0;
      this.cdRef.markForCheck();
    }, 0);
  }

  ngOnDestroy(): void {
    this.formClassObserver?.disconnect();
    this.optionClickSubscriptions.unsubscribe();
  }

  get resolvedMaxLength(): number | null {
    const explicitMaxLength = this.toNumberOrNull(this.maxLength);
    if (explicitMaxLength !== null) {
      return explicitMaxLength;
    }

    return this.toNumberOrNull(this.elementRef.nativeElement.getAttribute('maxLength'));
  }

  onSearchInput(text: string, target?: HTMLElement | HTMLInputElement): void {
    const limitedText = this.applyMaxLength(text, target);
    const trimmedText = limitedText.trim();
    const normalizedText = trimmedText.toLowerCase();
    this.searchChange.emit(trimmedText);

    if (this.externalSearch) return;

    let noResults = true;

    this.getNeededOptionsList().forEach(option => {
      if (this.multiple) {
        option.setHidden(!option.label.toLowerCase().includes(normalizedText) || this.isOptionSelected(option));
      } else option.setHidden(!option.label.toLowerCase().includes(normalizedText));

      if (!option.isHidden) noResults = false;
    })

    this.isNoResults = noResults;
    this.cdRef.markForCheck();
  }

  onOptionClicked(option: CustomSelectOptionComponent): void {
    if (option.disabled) return;

    if (this.multiple) {
      this.selectedOptions.push(option);
      if (this.selectedOptions.length === this.getNeededOptionsList().length) this.allAdded = true;

      option.setHidden(true);
    } else {
      this.resetOptionsStatus();
      this.selectedOption = option;
      this.previewHtml = Object.create(option.sanitizedHtml);
    }

    option.setSelected(true);

    this.isOpened = false;
    this.resetExternalSearch();

    if (this.multiple) {
      this.emitChanges();
    } else {
      this.onChange(this.selectedOption.value);
      this.change.emit(this.selectedOption.value);
    }

    this.cdRef.markForCheck();
  }

  resetOptionsStatus() {
    this.getNeededOptionsList().forEach(o => {
      o.setSelected(false);
      o.setHidden(false);
    });

    this.allAdded = false;

    if (this.multiple) this.selectedOptions = [];
    else {
      this.selectedOption = null;
      this.previewHtml = '';
    }

    this.cdRef.markForCheck();
  }

  clearAll() {
    this.resetOptionsStatus();

    if (this.multiple) {
      this.onChange([]);
      this.change.emit([]);
    }
    else {
      this.onChange(null);
      this.change.emit(null);
    }

    this.clear.emit();
  }

  getItemLabel(option: any): any {
    if (!this.bindLabel) {
      return option;
    }

    return this.bindLabel
      .split('.')
      .reduce((current, key) => current?.[key], option);
  }

  getItemValue(option: any) {
    if (!this.bindValue) {
      return option;
    }

    return this.bindValue
      .split('.')
      .reduce((current, key) => current?.[key], option);
  }

  getNeededOptionsList() {
    return this.items ? this.viewOptions : this.projectedOptions;
  }

  isOptionSelected(option: CustomSelectOptionComponent) {
    if (this.multiple) return !!this.selectedOptions.find(opt => this.valuesMatch(opt.value, option.value));
    else return this.valuesMatch(this.selectedOption?.value, option.value);
  }

  isOptionHided(option: CustomSelectOptionComponent) {
    if (!option) return false;

    let opt = this.multiple ? this.selectedOptions.find(opt => this.valuesMatch(opt.value, option.value)) : this.selectedOption;
    if (!opt) return false;

    if (this.multiple && this.isOptionSelected(opt)) return true;
    else return opt.isHidden;
  }

  private valuesMatch(a: any, b: any): boolean {
    if (a === b) return true;
    if (a == null || b == null) return false;

    if (typeof a === 'object' && typeof b === 'object') {
      const aId = a.id ?? a.publicId;
      const bId = b.id ?? b.publicId;
      if (aId != null && bId != null) return aId === bId;
    }

    return JSON.stringify(a) === JSON.stringify(b);
  }

  private emitChanges() {
    const options = this.selectedOptions.map(e => e.value);
    this.onChange(options);
    this.change.emit(options);
  }

  deselectOption(option: CustomSelectOptionComponent) {
    option.setSelected(false);
    option.setHidden(false);
    this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1);
    this.allAdded = false;

    this.emitChanges();

    setTimeout(() => {
      this.cdkOverlay.overlayRef.updatePosition();
      this.cdRef.markForCheck();
    })
  }

  addAll() {
    this.selectedOptions = [];

    this.getNeededOptionsList().forEach(option => {
      this.selectedOptions.push(option);
      option.setHidden(true);
    })

    this.isOpened = false;
    this.allAdded = true;

    this.emitChanges();
  }

  isShowClearBtn() {
    if (this.hideIcons || !this.clearable) return false;

    if (this.multiple) {
      return this.selectedOptions.length > 0;
    } else return !!this.selectedOption;
  }

  isShowArrow() {
    if (this.hideIcons || !this.clearable) return true;

    if (this.multiple) {
      return this.selectedOptions.length === 0;
    } else return !this.selectedOption;
  }

  updateOverlayPosition() {
    if (!this.fixedOverlayVerticalPlacement) {
      this.updateAutoDeterminedDropdownPosition();
    }

    this.cdkOverlay.overlayRef.updatePosition();
  }

  getOverlayPosition() {
    if (this.fixedOverlayVerticalPlacement === 'above') {
      return this.OVERLAY_POSITION_TOP;
    }

    if (this.fixedOverlayVerticalPlacement === 'below') {
      return this.OVERLAY_POSITION_BOTTOM;
    }

    return this.autoDeterminedDropdownPosition === 'above'
      ? this.OVERLAY_POSITION_TOP
      : this.OVERLAY_POSITION_BOTTOM;
  }

  onKeydown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();

      if (this.selectFirstOnEnter && this.searchable && !this.multiple && this.isOpened) {
        const firstOption = this.getNeededOptionsList()?.find(option => !option.isHidden && !option.disabled);
        if (firstOption) this.onOptionClicked(firstOption);
      }
    }
  }

  private resetExternalSearch(): void {
    if (this.externalSearch) this.searchChange.emit('');
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const text = event.clipboardData
      ? event.clipboardData.getData('text/plain')
      : (window as any).clipboardData.getData('Text');

    const singleLine = text.replace(/[\r\n]+/g, ' ');

    document.execCommand('insertText', false, singleLine);
  }

  OVERLAY_POSITION_TOP: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'top',
      overlayX: 'start',
      overlayY: 'bottom',
      offsetY: -2
    }
  ];

  OVERLAY_POSITION_BOTTOM: ConnectedPosition[] = [
    {
      originX: 'start',
      originY: 'bottom',
      overlayX: 'start',
      overlayY: 'top',
      offsetY: 2
    }
  ];
  protected readonly JSON = JSON;

  private syncSubmittedState(forceSubmitted = false): void {
    this.isFormSubmitted = forceSubmitted
      || !!this.ngForm?.submitted
      || !!this.formGroupDir?.submitted
      || this.isClosestFormSubmitted();
  }

  private observeClosestFormSubmittedClass(): void {
    const formElement = this.elementRef.nativeElement.closest('form');
    if (!formElement) {
      return;
    }

    this.formClassObserver = new MutationObserver(() => {
      const submitted = this.isClosestFormSubmitted();
      if (submitted !== this.isFormSubmitted) {
        this.isFormSubmitted = submitted;
        this.cdRef.markForCheck();
      }
    });

    this.formClassObserver.observe(formElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  private isClosestFormSubmitted(): boolean {
    const formElement = this.elementRef.nativeElement.closest('form');
    return !!formElement?.classList?.contains('ng-submitted');
  }

  private bindOptionClickHandlers(options: QueryList<CustomSelectOptionComponent>): void {
    this.optionClickSubscriptions.unsubscribe();
    this.optionClickSubscriptions = new Subscription();

    options.forEach(option => {
      this.optionClickSubscriptions.add(
        option.click.subscribe(() => this.onOptionClicked(option))
      );
    });
  }

  private applyMaxLength(text: string, target?: HTMLElement | HTMLInputElement): string {
    const maxLength = this.resolvedMaxLength;
    if (maxLength === null || text.length <= maxLength) {
      return text;
    }

    const limitedText = text.slice(0, maxLength);

    if (target instanceof HTMLInputElement && target.value !== limitedText) {
      target.value = limitedText;
    }

    if (target instanceof HTMLElement && !(target instanceof HTMLInputElement) && target.textContent !== limitedText) {
      target.textContent = limitedText;
      this.placeCaretAtEnd(target);
    }

    return limitedText;
  }

  private updateAutoDeterminedDropdownPosition(): void {
    const triggerElement = this.trigger?.elementRef?.nativeElement as HTMLElement | undefined;
    const anchorElement = triggerElement ?? this.elementRef.nativeElement;
    const triggerRect = anchorElement.getBoundingClientRect();
    const triggerCenterY = triggerRect.top + triggerRect.height / 2;

    this.autoDeterminedDropdownPosition =
      triggerCenterY > window.innerHeight / 2
        ? 'above'
        : 'below';
  }

  private placeCaretAtEnd(element: HTMLElement): void {
    const selection = window.getSelection();
    if (!selection) {
      return;
    }

    const range = document.createRange();
    range.selectNodeContents(element);
    range.collapse(false);
    selection.removeAllRanges();
    selection.addRange(range);
  }

  private toNumberOrNull(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  getSelectedOptionDisplayLabel(option: CustomSelectOptionComponent): string {
    return option?.value?.name
      || option?.value?.label
      || option?.label
      || '';
  }
}
